import { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Clock } from 'lucide-react';
import { api } from '@/api/apiClient';

const STEPS = [
  { key: 'created',   label: 'تم الإنشاء',    states: ['draft', 'sent', 'sale', 'done', 'cancel'] },
  { key: 'confirmed', label: 'تم التأكيد',     states: ['sale', 'done'] },
  { key: 'picking',   label: 'قيد التحضير',    states: ['sale', 'done'] },
  { key: 'delivered', label: 'تم التسليم',     states: ['done'] },
];

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
}

export default function OrderTimeline({ order }) {
  const state = order.state;
  // Odoo 17 has no 'done' SO state — an order stays 'sale' even after it
  // ships, so we can't infer delivery from `state`. We fetch the real
  // delivery progress (computed from the outgoing pickings) and drive the
  // "قيد التحضير" / "تم التسليم" steps from it.
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!order?.id || (state !== 'sale' && state !== 'done')) return;
    (async () => {
      try {
        const res = await api.get('/sellgo_order_progress', { order_id: order.id });
        if (alive && res?.success) setProgress(res.data);
      } catch { /* leave progress null → fall back to state-only behaviour */ }
    })();
    return () => { alive = false; };
  }, [order?.id, state]);

  const delivered = progress?.delivered === true;
  const inPrep = progress?.in_preparation === true;

  const getStepStatus = (step) => {
    if (step.key === 'created') return 'done';
    if (step.key === 'confirmed') return (state === 'sale' || state === 'done') ? 'done' : (state === 'cancel' ? 'cancelled' : 'pending');
    if (step.key === 'picking') {
      if (delivered) return 'done';            // already shipped → preparation is done
      if (state === 'sale' || inPrep) return 'active';
      return state === 'done' ? 'done' : 'pending';
    }
    if (step.key === 'delivered') {
      if (delivered || state === 'done') return 'done';
      return 'pending';
    }
    return 'pending';
  };

  const getStepTime = (step) => {
    if (step.key === 'created') return formatDate(order.date_order);
    if (step.key === 'confirmed' && (state === 'sale' || state === 'done')) return formatDate(order.date_order);
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-2xl px-4 py-4">
      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4">مسار الطلب</p>
      <div className="relative">
        {/* Vertical connector */}
        <div className="absolute right-[15px] top-4 bottom-4 w-0.5 bg-border" />
        <div className="space-y-4">
          {STEPS.map((step) => {
            const status = getStepStatus(step);
            const time = getStepTime(step);
            return (
              <div key={step.key} className="flex items-center gap-3">
                <div className="flex-shrink-0 z-10">
                  {status === 'done' && (
                    <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center shadow-sm">
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {status === 'active' && (
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center shadow-md animate-pulse">
                      <Clock className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {(status === 'pending' || status === 'cancelled') && (
                    <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                      <Circle className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1 flex items-center justify-between">
                  <p className={`text-sm font-semibold ${
                    status === 'done' ? 'text-foreground' :
                    status === 'active' ? 'text-primary' :
                    'text-muted-foreground'
                  }`}>{step.label}</p>
                  {time && <p className="text-xs text-muted-foreground">{time}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}